import { expVal, expValEquals } from '@confluence/feature-experiments';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { getLogger } from '@confluence/logger';

import type { EditorCommentType, EditorCommentMode } from './commentTypes.ts';

export const OUT_OF_DATE_KEY = 'outOfDate';

export const isOptimisticSaveEnabled = (): boolean =>
	expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) &&
	(expValEquals('confluence_fe_create_comment_optimistic', 'isEnabled', true) ||
		expValEquals('confluence_fe_reply_comment_optimistic', 'isEnabled', true) ||
		expValEquals('confluence_fe_edit_comment_optimistic', 'isEnabled', true));

export const shouldUseCommentDraftForOptimisticSave = (
	// TODO: (COMMENTS-6792) `type` is no longer needed. Remove this later.
	_type: EditorCommentType,
	mode: EditorCommentMode,
): boolean => {
	if (!isOptimisticSaveEnabled()) {
		return false;
	}
	switch (mode) {
		case 'create':
			return expValEquals('confluence_fe_create_comment_optimistic', 'isEnabled', true);
		case 'reply':
			return expValEquals('confluence_fe_reply_comment_optimistic', 'isEnabled', true);
		case 'edit':
		default: // `mode` can only be 'create' | 'reply' | 'edit'
			return expValEquals('confluence_fe_edit_comment_optimistic', 'isEnabled', true);
	}
};

type DraftObject = {
	pageId: string;
	adf: string;
};

const logger = getLogger('comment-draft-utils');

// Exported only for testing
export const getKey = (type: EditorCommentType, mode: EditorCommentMode): string => {
	if (isOptimisticSaveEnabled()) {
		switch (type) {
			// Treat edit-inline and inline as the same type, so a comment draft
			// saved in EDITOR mode can be used in VIEW mode of the same page.
			case 'edit-inline':
			case 'inline':
				if (mode === 'create') {
					return keys.COMMENT_DRAFTS_INLINE_CREATE;
				} else if (mode === 'edit') {
					return keys.COMMENT_DRAFTS_INLINE_EDIT;
				} else {
					return keys.COMMENT_DRAFTS_INLINE_REPLY;
				}
			case 'page':
			default:
				if (mode === 'create') {
					return keys.COMMENT_DRAFTS_PAGE_CREATE;
				} else if (mode === 'edit') {
					return keys.COMMENT_DRAFTS_PAGE_EDIT;
				} else {
					return keys.COMMENT_DRAFTS_PAGE_REPLY;
				}
		}
	}

	// TODO: (COMMENTS-6792) Remove this when optimistic save is rolled out
	switch (type) {
		case 'edit-inline':
			if (mode === 'create') {
				return keys.COMMENT_DRAFTS_EDITOR_INLINE_CREATE;
			} else if (mode === 'edit') {
				return keys.COMMENT_DRAFTS_EDITOR_INLINE_EDIT;
			} else {
				return keys.COMMENT_DRAFTS_EDITOR_INLINE_REPLY;
			}
		case 'inline':
			if (mode === 'create') {
				return keys.COMMENT_DRAFTS_INLINE_CREATE;
			} else if (mode === 'edit') {
				return keys.COMMENT_DRAFTS_INLINE_EDIT;
			} else {
				return keys.COMMENT_DRAFTS_INLINE_REPLY;
			}
		case 'page':
		default:
			if (mode === 'create') {
				return keys.COMMENT_DRAFTS_PAGE_CREATE;
			} else if (mode === 'edit') {
				return keys.COMMENT_DRAFTS_PAGE_EDIT;
			} else {
				return keys.COMMENT_DRAFTS_PAGE_REPLY;
			}
	}
};

export const saveCommentDraft = (
	adf: string,
	pageId: string,
	type: EditorCommentType,
	mode: EditorCommentMode,
): boolean => {
	let b64EncodedDraftObj: string | null = null;
	const key = getKey(type, mode);
	const draftObj = JSON.stringify({ pageId, adf });
	try {
		if (isOptimisticSaveEnabled()) {
			b64EncodedDraftObj = adfStringToBase64(draftObj);
		} else {
			b64EncodedDraftObj = btoa(draftObj);
		}
	} catch (e) {
		logger.error`Unable to save draft object for ${pageId}; ${e}`;
	}

	if (b64EncodedDraftObj === null) {
		return false;
	}

	// Store the draft with a 72 hour expiration
	localStorage.setItem(key, b64EncodedDraftObj, 259200);
	return true;
};

export const clearCommentDraft = (type: EditorCommentType, mode: EditorCommentMode): void => {
	const key = getKey(type, mode);

	localStorage.removeItem(key);
};

export const hasCommentDraft = (
	pageId: string,
	type: EditorCommentType,
	mode: EditorCommentMode,
): boolean => Boolean(getCommentDraft(pageId, type, mode));

export const getCommentDraft = (
	pageId: string,
	type: EditorCommentType,
	mode: EditorCommentMode,
): string | null => {
	let draftObj: DraftObject | null = null;
	const key = getKey(type, mode);
	const b64EncodedDraftObj = localStorage.getItem(key);

	if (b64EncodedDraftObj !== null) {
		try {
			if (isOptimisticSaveEnabled()) {
				draftObj = JSON.parse(base64ToAdfString(b64EncodedDraftObj));
			} else {
				draftObj = JSON.parse(atob(b64EncodedDraftObj));
			}
		} catch (e) {
			logger.error`Unable to get draft object for ${pageId}; ${e}`;
		}

		if (draftObj) {
			const { pageId: draftPageId, adf } = draftObj;

			// If the pageId matches, return the ADF, otherwise return null
			if (draftPageId === pageId) {
				return adf;
			}
		}
	}

	return null;
};

// Below are the proper way to convert between an ADF string and base64.
// ADF string can contain unicode characters, so simply using btoa() and atob()
// will break sometimes.
// Exported only for testing
export const adfStringToBase64 = (adfString: string): string => {
	const bytes = new TextEncoder().encode(adfString);
	let binary = '';
	for (let i = 0; i < bytes.byteLength; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return btoa(binary);
};

// Exported only for testing
export const base64ToAdfString = (base64: string): string => {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return new TextDecoder().decode(bytes);
};
